import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClockIcon } from '@heroicons/react/24/outline'
import type { SpinShowcaseEntry } from '../../../Generated/SpinShowcaseEntry.ts'
import type { Taxas } from '../../../Model/ITaxa.ts'
import { fetchSpinShowcase } from '../../../Repository/Visualization/SpinMeta.js'
import { identityFor, identityKeySvg } from '../../Component/Charts/Options/visualIdentity.js'
import { SpinIdCardIcon } from '../../Component/SpinIdCard/SpinIdCardIcon.js'
import { AmpliconWidget } from '../OrderManagementPage/AmpliconWidget.js'
import { Section } from './SpinSummarySections.js'
import { SpinUuidLookup } from './SpinUuidLookup.js'
import { SpinInsightNudge } from './SpinInsightNudge.js'
import { RANK_LABELS, badgeName, headline, isItalicRank } from './spinSummary.logic.js'

const ShowcaseCard = ({ entry }: { entry: SpinShowcaseEntry }) => {
  const head = headline(entry.display)
  const badge = badgeName(entry.display)
  const path = (entry.display?.ranks ?? []).filter((r) => r.value && r.rank >= 2 && r.rank < (head?.rank ?? 0)).map((r) => r.value)
  return (
    <Link
      to={`/spins/${entry.spinId}`}
      className="flex gap-3 rounded-xl border border-emerald-100 bg-white shadow-sm p-4 hover:shadow-md hover:border-emerald-300 transition"
    >
      <span
        aria-hidden="true"
        className="w-12 h-12 shrink-0 rounded-lg bg-emerald-50 flex items-center justify-center"
        title={badge ? `Chart colour of ${badge}` : undefined}
        // identityKeySvg emits only colours and fixed geometry, never the name.
        dangerouslySetInnerHTML={{ __html: badge ? identityKeySvg(identityFor(badge), { size: 32, lineLength: 0 }) : '' }}
      />
      <span className="min-w-0">
        <span className="block text-xs text-gray-500">
          <AmpliconWidget amplicon={entry.amplicon as Taxas} showIcon={false} />
        </span>
        <span className={`block text-lg font-bold text-gray-900 truncate ${head && isItalicRank(head.rank) ? 'italic' : ''}`}>
          {head?.name ?? 'Unresolved'}
        </span>
        <span className="block text-xs text-gray-500 truncate">{path.join(' › ')}</span>
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-600 mt-1 [&>span]:whitespace-nowrap">
          {head && <span>{RANK_LABELS[head.rank]} level</span>}
          <span>· {entry.memberCount.toLocaleString()} members</span>
          {entry.hasLineage && (
            <span className="inline-flex items-center gap-0.5" title="Has a lineage">
              · <ClockIcon className="w-3.5 h-3.5" /> lineage
            </span>
          )}
        </span>
      </span>
    </Link>
  )
}

/** Public entry point to the SPIN pages (`/spins`): look a SPIN up by its uuid, or open an example. */
export const SpinLandingPage = () => {
  const [examples, setExamples] = useState<SpinShowcaseEntry[]>([])

  useEffect(() => {
    let cancelled = false
    fetchSpinShowcase().then((e) => !cancelled && setExamples(e))
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4">
      <header>
        <h1 className="text-3xl font-bold">SPINs</h1>
        <p className="text-sm text-gray-600 mt-2">
          A SPIN is a canonical species cluster: one persistent identity for closely related OTUs across orders. Enter a SPIN uuid to open
          its definition and resolved taxonomy.
        </p>
        <SpinInsightNudge />
      </header>
      <div className="space-y-4 bg-emerald-50/70 rounded-2xl p-3 md:p-5">
        <section className="bg-white rounded-xl border border-emerald-100 shadow-md p-5 flex items-center gap-6">
          <SpinIdCardIcon variant="display" className="hidden sm:block w-28 md:w-36 shrink-0 text-emerald-700" />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Look up a SPIN</h2>
            <SpinUuidLookup toPath={(id) => `/spins/${id}`} />
          </div>
        </section>
        {examples.length > 0 && (
          <Section title="Example SPINs" hint="Well-resolved SPINs from recent clustering runs, one per amplicon.">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {examples.map((e) => (
                <ShowcaseCard key={e.spinId} entry={e} />
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
